/**
 * HelpFeedbackButton.tsx
 *
 * A persistent floating button (bottom-right) that opens a Help & Feedback
 * modal. Teachers can submit questions, bug reports, feature suggestions, or
 * contact requests from anywhere in the app.
 *
 * Each submission is stored server-side in the feedback table and includes:
 *   • authenticated user ID, name, email
 *   • category, message
 *   • current page & open resource context
 *   • browser / device info
 *   • app version
 *   • optional screenshot attachment (image, ≤ 2 MB)
 *   • status: new → in_review → responded → closed
 */

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HelpCircle, Bug, Lightbulb, Headphones, X,
  Paperclip, Send, CheckCircle2, AlertCircle, MessageCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// ── Types ─────────────────────────────────────────────────────────────────────

type FeedbackCategory = 'question' | 'bug' | 'feature' | 'support';

const CATEGORIES: {
  id: FeedbackCategory;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bg: string;
}[] = [
  {
    id: 'question',
    label: 'Ask a Question',
    description: 'Get help understanding a feature',
    icon: HelpCircle,
    color: 'text-teal-600',
    bg: 'bg-teal-50 border-teal-200 hover:bg-teal-100',
  },
  {
    id: 'bug',
    label: 'Report a Bug',
    description: 'Something isn\'t working as expected',
    icon: Bug,
    color: 'text-red-500',
    bg: 'bg-red-50 border-red-200 hover:bg-red-100',
  },
  {
    id: 'feature',
    label: 'Suggest a Feature',
    description: 'Share an idea to improve TeachBud',
    icon: Lightbulb,
    color: 'text-amber-500',
    bg: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
  },
  {
    id: 'support',
    label: 'Contact Support',
    description: 'Account, billing, or other questions',
    icon: Headphones,
    color: 'text-blue-500',
    bg: 'bg-blue-50 border-blue-200 hover:bg-blue-100',
  },
];

const PLACEHOLDERS: Record<FeedbackCategory, string> = {
  question: 'What would you like to know? Describe what you\'re trying to do and we\'ll help you out.',
  bug: 'Describe the bug — what happened, what you expected, and how to reproduce it. Include the page you were on if relevant.',
  feature: 'Describe your idea. What problem would it solve? How would it make TeachBud more useful for you?',
  support: 'Describe your issue in detail. For account or billing questions, include any relevant account info.',
};

// ── Config ────────────────────────────────────────────────────────────────────

const APP_VERSION = '0.1.0-beta';
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024; // 2 MB

const BASE_PATH = import.meta.env.BASE_URL?.replace(/\/$/, '') ?? '';
const API_URL = `${BASE_PATH}/api/feedback`;

// ── Props ─────────────────────────────────────────────────────────────────────

export interface HelpFeedbackButtonProps {
  /** Teacher's display name. Optional — userId from Clerk JWT is always server-enforced. */
  userName?: string;
  /** Teacher's email. Optional — included in feedback record for follow-up. */
  userEmail?: string;
  currentPage: string;
  resourceType?: string | null;
  resourceId?: string | null;
  /** Open the modal immediately on mount (e.g. from an error boundary). */
  initialOpen?: boolean;
  /** Pre-select a category when the modal opens. */
  initialCategory?: FeedbackCategory;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function HelpFeedbackButton({
  userName = '',
  userEmail = '',
  currentPage,
  resourceType,
  resourceId,
  initialOpen = false,
  initialCategory,
}: HelpFeedbackButtonProps) {
  const [isOpen, setIsOpen]               = useState(initialOpen);
  const [category, setCategory]           = useState<FeedbackCategory | null>(initialCategory ?? null);
  const [message, setMessage]             = useState('');
  const [attachment, setAttachment]       = useState<{ dataUrl: string; name: string } | null>(null);
  const [attachError, setAttachError]     = useState<string | null>(null);
  const [submitting, setSubmitting]       = useState(false);
  const [submitted, setSubmitted]         = useState(false);
  const [submitError, setSubmitError]     = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Helpers ──────────────────────────────────────────────────────────────

  function openModal() {
    setIsOpen(true);
    setCategory(initialCategory ?? null);
    setMessage('');
    setAttachment(null);
    setAttachError(null);
    setSubmitted(false);
    setSubmitError(null);
  }

  function closeModal() {
    if (submitting) return;
    setIsOpen(false);
  }

  function handleFile(file: File) {
    setAttachError(null);
    if (!file.type.startsWith('image/')) {
      setAttachError('Only image files (PNG, JPG, WEBP) are accepted as attachments.');
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachError('Image is too large. Maximum size is 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setAttachment({ dataUrl, name: file.name });
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit() {
    if (!category || !message.trim() || submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      category,
      message: message.trim(),
      currentPage,
      resourceType:  resourceType  ?? undefined,
      resourceId:    resourceId    ?? undefined,
      userName,
      userEmail,
      browserInfo:   navigator.userAgent,
      deviceInfo:    `${navigator.platform} — ${window.screen.width}×${window.screen.height}`,
      appVersion:    APP_VERSION,
      attachment:    attachment?.dataUrl ?? undefined,
    };

    try {
      const resp = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as Record<string, string>;
        throw new Error(body.error ?? `Server error ${resp.status}`);
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError((err as Error).message ?? 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const selectedCat = category ? CATEGORIES.find(c => c.id === category) : null;
  const canSubmit = !!category && message.trim().length >= 5 && !submitting;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={openModal}
        title="Help & Feedback"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 h-11 px-4 rounded-full
          bg-white border border-slate-200 shadow-lg hover:shadow-xl hover:border-primary/30
          text-slate-600 hover:text-primary text-sm font-semibold
          transition-all duration-200 group"
      >
        <MessageCircle size={16} className="text-primary transition-transform group-hover:scale-110" />
        <span>Help & Feedback</span>
      </button>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={closeModal}
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg z-10 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MessageCircle size={15} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 leading-tight">Help & Feedback</p>
                    <p className="text-xs text-slate-400">We read everything — usually respond within 1 business day</p>
                  </div>
                </div>
                <button onClick={closeModal}
                  className="h-8 w-8 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex items-center justify-center transition-all">
                  <X size={16} />
                </button>
              </div>

              {submitted ? (
                /* ── Success state ── */
                <div className="p-8 flex flex-col items-center gap-4 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 size={28} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-base font-bold text-slate-900 mb-1">Thank you!</p>
                    <p className="text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">
                      Your {selectedCat?.label.toLowerCase() ?? 'feedback'} has been received.
                      We'll follow up at <span className="font-medium text-slate-700">{userEmail || 'your email'}</span>.
                    </p>
                  </div>
                  <button onClick={closeModal}
                    className="mt-2 h-9 px-5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">
                    Done
                  </button>
                </div>
              ) : (
                /* ── Form ── */
                <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">

                  {/* Category picker */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2.5">
                      What can we help with?
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {CATEGORIES.map(cat => {
                        const Icon = cat.icon;
                        const selected = category === cat.id;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => {
                              setCategory(cat.id);
                              setSubmitError(null);
                            }}
                            className={`flex items-center gap-2.5 px-3 py-3 rounded-xl border text-left transition-all ${cat.bg}
                              ${selected ? 'ring-2 ring-offset-1 ring-primary/40 border-primary/30' : ''}`}
                          >
                            <Icon size={16} className={cat.color} />
                            <div>
                              <p className="text-xs font-bold text-slate-800 leading-tight">{cat.label}</p>
                              <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">{cat.description}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Message */}
                  <AnimatePresence>
                    {category && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
                              {selectedCat?.label}
                            </label>
                            <textarea
                              autoFocus
                              value={message}
                              onChange={e => setMessage(e.target.value)}
                              placeholder={PLACEHOLDERS[category]}
                              rows={5}
                              className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm
                                text-slate-800 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-primary/20
                                focus:border-primary/40 focus:bg-white transition-all"
                            />
                            <p className={`text-xs mt-1 text-right ${message.trim().length < 5 && message.length > 0 ? 'text-red-400' : 'text-slate-400'}`}>
                              {message.trim().length} chars {message.trim().length < 5 ? '(min 5)' : ''}
                            </p>
                          </div>

                          {/* Attachment */}
                          <div>
                            <button
                              onClick={() => fileRef.current?.click()}
                              className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-primary transition-colors"
                            >
                              <Paperclip size={13} />
                              {attachment ? attachment.name : 'Attach a screenshot (optional, ≤ 2 MB)'}
                            </button>
                            {attachment && (
                              <div className="flex items-center gap-2 mt-1.5">
                                <img
                                  src={attachment.dataUrl}
                                  alt="Attachment preview"
                                  className="h-12 w-20 object-cover rounded-lg border border-slate-200"
                                />
                                <button
                                  onClick={() => setAttachment(null)}
                                  className="text-xs text-red-400 hover:text-red-600 transition-colors"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                            {attachError && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle size={11} /> {attachError}
                              </p>
                            )}
                            <input
                              ref={fileRef}
                              type="file"
                              accept="image/*"
                              className="sr-only"
                              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
                            />
                          </div>

                          {/* Context badge (what page / resource) */}
                          <p className="text-[10px] text-slate-400 leading-relaxed">
                            Sending from: <span className="font-medium text-slate-500">{currentPage}</span>
                            {resourceType && resourceId && (
                              <> · {resourceType} #{resourceId}</>
                            )}
                          </p>

                          {/* Privacy notice */}
                          <p className="text-[10px] text-slate-400">
                            By submitting, you agree to our{' '}
                            <a href={`${BASE_PATH}/privacy`} target="_blank" rel="noopener noreferrer"
                              className="underline hover:text-slate-600 transition-colors">Privacy Policy</a>.
                          </p>

                          {/* Submit error */}
                          {submitError && (
                            <p className="text-xs text-red-600 flex items-center gap-1.5 bg-red-50 rounded-xl px-3 py-2">
                              <AlertCircle size={12} /> {submitError}
                            </p>
                          )}

                          {/* Submit */}
                          <div className="flex justify-end pt-1">
                            <Button
                              onClick={handleSubmit}
                              disabled={!canSubmit}
                              className="rounded-xl bg-primary hover:bg-primary/90 text-white gap-2 disabled:opacity-40"
                            >
                              {submitting ? (
                                <>
                                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                  </svg>
                                  Sending…
                                </>
                              ) : (
                                <><Send size={14} /> Send {selectedCat?.label ?? 'Feedback'}</>
                              )}
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
